/**
 * One session inside a project, in the coding rail.
 *
 * Extracted from the rail's inline markup when the flat list became a tree: it
 * is now rendered from two places (a project's children, and the flat fallback
 * when there is nothing to group), and the status mark below is the kind of
 * thing that quietly diverges once it is copied.
 *
 * @module components/coding/sidebar/session-row
 */

import { GitBranch, Loader2 } from "lucide-react"
import { Link } from "react-router-dom"

import { SidebarMenuSubButton, SidebarMenuSubItem } from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"
import type { CodeSessionState, CodeSessionSummary } from "@/services/code-service"

/**
 * The rail's status mark, which answers two questions at a glance: is this
 * sandbox costing anything right now, and is the agent still working?
 *
 * A spinner for the states where something is happening, a dot otherwise —
 * green for a live sandbox, grey for one that is asleep or has never come up,
 * red for a failure. `archived` gets nothing: it is gone, not idle.
 */
export function SessionStatus({ state }: { state: CodeSessionState }) {
  if (state === "provisioning" || state === "running") {
    return (
      <Loader2
        className="ml-auto size-3 shrink-0 animate-spin text-primary"
        aria-label={state === "running" ? "Working" : "Starting"}
      />
    )
  }

  if (state === "archived") return null

  const tone =
    state === "ready"
      ? "bg-success"
      : state === "failed"
        ? "bg-destructive"
        : "bg-muted-foreground/40"

  const label =
    state === "ready" ? "Sandbox running" : state === "failed" ? "Failed" : "Asleep"

  return (
    <span
      className={cn("ml-auto size-2 shrink-0 rounded-full", tone)}
      title={label}
      aria-label={label}
    />
  )
}

export interface SessionRowProps {
  session: CodeSessionSummary
  basePath: string
  isActive: boolean
}

export function SessionRow({ session, basePath, isActive }: SessionRowProps) {
  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        asChild
        isActive={isActive}
        // The repository is the row's parent now, so it is no longer repeated
        // as a subtitle — which is what let the row drop to a single line.
        title={`${session.title} — ${session.repoOwner}/${session.repoName}`}
      >
        <Link
          data-testid={`coding-session-${session.id}`}
          to={`${basePath}/${session.id}`}
        >
          <GitBranch className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{session.title}</span>
          <SessionStatus state={session.state} />
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
}

SessionRow.displayName = "SessionRow"
